import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router";
import {
  Zap,
  Layers,
  Palette,
  Globe,
  Rocket,
  ArrowRight,
  Monitor,
  Tablet,
  Smartphone,
  ArrowLeft,
  Loader2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import type { SiteDocument } from "@zirro/schema";
import { DocumentRenderer } from "@zirro/renderer";
import {
  EditorLayout,
  useEditorStore,
  saveDraftToStorage,
  loadDraftFromStorage,
} from "@zirro/editor";
import { ApiError, apiFetch, type Product, type Site } from "../lib/api";
import { useAuthStore } from "../stores/auth";
import { uploadSiteImage } from "../lib/media";
import { useProducts, useStore } from "../lib/store-api";
import { ProductEditor } from "./store/ProductEditor";
import { Button } from "../components/ui/Button";
import { Spinner } from "../components/ui/Spinner";
import { PublishSuccessModal } from "../components/ui/PublishSuccessModal";
import { HeaderActions } from "./public/store/Cart";
import {
  StorefrontProvider,
  hasCatalogSlot,
} from "./public/store/StorefrontContext";
import { renderStoreSlot } from "./public/store/slots";
import { Catalog } from "./public/store/Catalog";
import {
  type StoreRoute,
  storeRouteFor,
  renderStoreRoute,
} from "./public/store/routes";
import { usePreviewStore } from "./public/store/usePreviewStore";
import { useTemplate } from "../lib/queries";
import { layoutsForSite } from "../lib/page-layouts";
import { ZirroLogo } from "../components/ui/ZirroLogo";
import type { Href } from "@zirro/schema";
import "./public/store/store.css";

/**
 * Small standalone pages. Auth pages live in ./auth/*; templates live in
 * ./templates/*; onboarding in ./onboarding/*; dashboard in ./dashboard/*.
 */

export function parseDocumentPayload(raw: unknown): SiteDocument | null {
  if (!raw) return null;
  if (typeof raw === "object" && raw !== null) {
    const doc = raw as SiteDocument;
    if (Array.isArray(doc.pages) && doc.pages.length > 0) {
      return doc;
    }
  }
  if (typeof raw === "string") {
    const str = raw.trim();
    if (!str) return null;
    try {
      const parsed = JSON.parse(str);
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        Array.isArray((parsed as SiteDocument).pages)
      ) {
        return parsed as SiteDocument;
      }
    } catch {
      // fallback to base64 decode
    }
    try {
      const decoded = atob(str);
      const parsed = JSON.parse(decoded);
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        Array.isArray((parsed as SiteDocument).pages)
      ) {
        return parsed as SiteDocument;
      }
    } catch {
      // could not parse
    }
  }
  return null;
}

const KEPT_LOCALLY = "Your changes are kept in this browser.";

/** Turns a failed draft save into a message saying what happened and what to do. */
function draftSaveErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.status) {
      case 401:
        return `Your session has expired. Sign in again to keep saving. ${KEPT_LOCALLY}`;
      case 403:
        return "Your role on this site doesn't allow editing. Ask an owner for editor access.";
      case 404:
        return "This site no longer exists or you no longer have access to it.";
      case 409:
        return `This site was changed in another tab or session. Reload the editor to get the latest version. ${KEPT_LOCALLY}`;
      case 413:
        return "The site is too large to save. Remove some content or images and try again.";
      case 422:
        return `Some changes couldn't be saved because the site data isn't valid (${err.message}). Undo the last change and try again.`;
      default:
        if (err.status >= 500) {
          return `The server couldn't save your changes. Try again in a moment. ${KEPT_LOCALLY}`;
        }
        return `${err.message || "Saving failed."} ${KEPT_LOCALLY}`;
    }
  }
  if (err instanceof TypeError) {
    return `Can't reach the server. Check your connection and try again. ${KEPT_LOCALLY}`;
  }
  return `Your changes couldn't be saved. Try again in a moment. ${KEPT_LOCALLY}`;
}

function Placeholder({ title, hint }: { title: string; hint?: string }) {
  return (
    <main className="page">
      <h1>{title}</h1>
      {hint ? <p className="text-muted">{hint}</p> : null}
      <p style={{ marginTop: "var(--z-space-4)" }}>
        <Link to="/">← Back home</Link>
      </p>
    </main>
  );
}

export function NotFoundPage() {
  return (
    <main
      className="page"
      style={{ textAlign: "center", paddingTop: "var(--z-space-24)" }}
    >
      <div
        style={{
          fontSize: "4rem",
          fontWeight: 800,
          color: "var(--z-gray-200)",
          marginBottom: "var(--z-space-4)",
        }}
      >
        404
      </div>
      <h1 style={{ marginBottom: "var(--z-space-3)" }}>Page not found</h1>
      <p className="text-muted" style={{ marginBottom: "var(--z-space-8)" }}>
        The page you're looking for doesn't exist or has been moved.
      </p>
      <Link className="btn btn--primary btn--lg" to="/">
        <ArrowLeft size={18} />
        Back to Zirro
      </Link>
    </main>
  );
}

export function HomePage() {
  const signedIn = useAuthStore((s) => s.status === "authenticated");
  return (
    <div>
      {/* Navigation */}
      <nav className="landing-nav">
        <Link to="/" className="landing-nav__logo" aria-label="Zirro home">
          <ZirroLogo product="Sites" />
        </Link>
        <div className="landing-nav__links">
          <Link to="/templates" className="btn btn--ghost">
            Templates
          </Link>
          {signedIn ? (
            <Link to="/app/dashboard" className="btn btn--primary">
              Go to Dashboard
            </Link>
          ) : (
            <>
              <Link to="/login" className="btn btn--ghost">
                Log in
              </Link>
              <Link to="/signup" className="btn btn--primary">
                Sign up
              </Link>
            </>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="landing-hero">
        <span className="landing-hero__badge">
          <Zap size={14} />
          No code required
        </span>
        <h1>Build a stunning website for your profession</h1>
        <p>
          Choose your profession, pick a beautiful template, customize
          everything visually, and publish — all in minutes. No coding, no
          complexity.
        </p>
        <div className="landing-hero__actions">
          <Link to="/app/onboarding" className="btn btn--primary btn--xl">
            Start building free
            <ArrowRight size={18} />
          </Link>
          <Link to="/templates" className="btn btn--secondary btn--xl">
            Browse templates
          </Link>
        </div>
      </section>

      {/* Features */}
      <section className="landing-features">
        <div className="landing-features__header">
          <h2>Everything you need to launch</h2>
          <p>Professional tools made simple for every profession.</p>
        </div>
        <div className="landing-features__grid">
          {[
            {
              icon: Layers,
              title: "Profession-tailored designs",
              desc: "Templates designed specifically for lawyers, developers, architects, creators, and more.",
            },
            {
              icon: Palette,
              title: "Visual editor",
              desc: "Drag, drop, and customize every element. Change colors, fonts, and layout with a click.",
            },
            {
              icon: Globe,
              title: "Instant publishing",
              desc: "Go live in seconds. Your website is hosted, fast, and accessible from anywhere.",
            },
            {
              icon: Smartphone,
              title: "Responsive by default",
              desc: "Every site looks perfect on desktop, tablet, and mobile — automatically.",
            },
          ].map((feature) => {
            const Icon = feature.icon;
            return (
              <div key={feature.title} className="landing-feature">
                <div className="landing-feature__icon">
                  <Icon size={28} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section className="landing-steps">
        <div className="landing-steps__inner">
          <div className="landing-steps__header">
            <h2>How it works</h2>
            <p>From zero to published in four simple steps.</p>
          </div>
          <div className="landing-steps__grid">
            {[
              {
                num: "1",
                title: "Choose your profession",
                desc: "Tell us what you do so we show you relevant designs.",
              },
              {
                num: "2",
                title: "Pick a template",
                desc: "Browse beautiful, profession-specific starting points.",
              },
              {
                num: "3",
                title: "Customize visually",
                desc: "Edit text, images, colors, and layout with our drag-and-drop editor.",
              },
              {
                num: "4",
                title: "Publish instantly",
                desc: "Hit publish and your website is live. Share it with the world.",
              },
            ].map((step) => (
              <div key={step.num} className="landing-step">
                <div className="landing-step__number">{step.num}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="landing-cta">
        <h2>Ready to build your website?</h2>
        <p>Start building your professional website in minutes.</p>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "var(--z-space-4)",
          }}
        >
          <Link to="/app/onboarding" className="btn btn--primary btn--xl">
            <Rocket size={18} />
            Start building free
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <p>
          © {new Date().getFullYear()} Zirro. Build your website without code.
        </p>
      </footer>
    </div>
  );
}

export function SitesPage() {
  // The dashboard owns site management from Phase 3 on (PRD §12).
  return <Navigate to="/app/dashboard" replace />;
}

export function SiteDetailPage() {
  const { siteId } = useParams();
  return <Placeholder title="Website" hint={`Site ${siteId ?? "unknown"}`} />;
}

interface ServerDraftResponse {
  id: string;
  revision: number;
  schemaVersion: number;
  document: SiteDocument | string;
}

export function EditorPage() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [initialDoc, setInitialDoc] = useState<SiteDocument | undefined>(
    undefined,
  );
  const [siteDetails, setSiteDetails] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishedVersion, setPublishedVersion] = useState<number | undefined>(
    undefined,
  );
  const revisionRef = useRef(1);
  const mode = useEditorStore((s) => s.mode);
  // The site's template offers alternative page designs in the Pages tab.
  const template = useTemplate(siteDetails?.templateId);
  // Store sites show their real products on the canvas instead of placeholders.
  const previewStore = usePreviewStore(siteDetails, template.data, initialDoc);
  const pageLayouts = useMemo(
    () =>
      layoutsForSite(
        template.data?.variations,
        initialDoc,
        siteDetails?.name ?? "",
      ),
    [template.data?.variations, initialDoc, siteDetails?.name],
  );
  // A real store's products can be edited from their cards on the canvas
  // (undefined = form closed, null = adding a new product).
  const ownStore = useStore(siteId);
  const ownProducts = useProducts(ownStore.data ? siteId : undefined);
  const [editingProduct, setEditingProduct] = useState<
    Product | null | undefined
  >(undefined);
  const editProduct = useCallback(
    (product: Product | null) =>
      // Cards show live (active) products; edit the owner's full record.
      setEditingProduct(
        product
          ? (ownProducts.data?.find((p) => p.id === product.id) ?? product)
          : null,
      ),
    [ownProducts.data],
  );
  const uploadImage = useCallback(
    (file: File) => {
      if (!siteId)
        return Promise.reject(
          new Error("Save the site before uploading images."),
        );
      return uploadSiteImage(siteId, file);
    },
    [siteId],
  );

  useEffect(() => {
    let unmounted = false;
    async function loadDraft() {
      if (!siteId) return;

      // Fetch site details for name/slug
      try {
        const siteData = await apiFetch<Site>(`/sites/${siteId}`);
        if (!unmounted) setSiteDetails(siteData);
      } catch (err) {
        console.warn("Could not load site details:", err);
      }

      // 1. Memory Store Check
      const storeState = useEditorStore.getState();
      if (
        storeState.siteId === siteId &&
        storeState.document?.pages?.length > 0
      ) {
        if (!unmounted) {
          setInitialDoc(storeState.document);
          setLoading(false);
        }
      } else {
        // 2. Local Storage Cache Check
        const localDoc = loadDraftFromStorage(siteId);
        if (localDoc && !unmounted) {
          setInitialDoc(localDoc);
          setLoading(false);
        }
      }

      // 3. Server Backend API Fetch
      try {
        const draft = await apiFetch<ServerDraftResponse>(
          `/sites/${siteId}/draft`,
        );
        if (!unmounted && draft?.document) {
          revisionRef.current = draft.revision ?? 1;
          const parsed = parseDocumentPayload(draft.document);
          if (parsed) {
            const currentStore = useEditorStore.getState();
            if (currentStore.siteId === siteId && currentStore.isDirty) {
              setInitialDoc(currentStore.document);
            } else {
              setInitialDoc(parsed);
              saveDraftToStorage(siteId, parsed);
            }
          }
        }
      } catch (err) {
        console.warn(
          "Could not load backend draft, retaining active local document:",
          err,
        );
      } finally {
        if (!unmounted) setLoading(false);
      }
    }
    loadDraft();
    return () => {
      unmounted = true;
    };
  }, [siteId]);

  const handleEditorPublish = async () => {
    if (!siteId) return;
    const currentDoc = useEditorStore.getState().document;
    if (currentDoc) {
      saveDraftToStorage(siteId, currentDoc);
      try {
        const res = await apiFetch<ServerDraftResponse>(
          `/sites/${siteId}/draft`,
          {
            method: "PUT",
            body: JSON.stringify({
              revision: revisionRef.current,
              schemaVersion: 1,
              document: currentDoc,
            }),
          },
        );
        if (res?.revision) revisionRef.current = res.revision;
      } catch (err) {
        console.warn("Autosave before publish failed, continuing:", err);
      }
    }

    try {
      const ver = await apiFetch<{ versionNumber: number }>(
        `/sites/${siteId}/publish`,
        {
          method: "POST",
        },
      );
      setPublishedVersion(ver.versionNumber);
      setPublishModalOpen(true);
    } catch (err: unknown) {
      alert(
        `Publishing failed: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
    }
  };

  if (loading) {
    return (
      <div className="loading-center" style={{ minHeight: "100vh" }}>
        <Spinner size="lg" />
        <h2>Loading Editor</h2>
        <p>Retrieving your site draft…</p>
      </div>
    );
  }

  const editor = (
    <EditorLayout
      siteId={siteId}
      renderSlot={previewStore ? renderStoreSlot : undefined}
      headerActions={previewStore ? <HeaderActions /> : undefined}
      pageLayouts={pageLayouts}
      onUploadImage={uploadImage}
      initialDocument={initialDoc}
      onPreview={() => {
        if (siteId) {
          const currentDoc = useEditorStore.getState().document;
          if (currentDoc) {
            saveDraftToStorage(siteId, currentDoc);
          }
        }
        navigate(`/app/sites/${siteId}/preview`);
      }}
      onPublish={handleEditorPublish}
      onSave={async (doc) => {
        if (!siteId) return;
        saveDraftToStorage(siteId, doc);
        try {
          const res = await apiFetch<ServerDraftResponse>(
            `/sites/${siteId}/draft`,
            {
              method: "PUT",
              body: JSON.stringify({
                revision: revisionRef.current,
                schemaVersion: 1,
                document: doc,
              }),
            },
          );
          if (res?.revision) {
            revisionRef.current = res.revision;
          }
        } catch (err) {
          console.error("Backend draft autosave error:", err);
          throw new Error(draftSaveErrorMessage(err), { cause: err });
        }
      }}
    />
  );

  return (
    <>
      {previewStore ? (
        <StorefrontProvider
          store={previewStore}
          interactive={mode === "preview"}
          onEditProduct={
            ownStore.data && mode === "edit" ? editProduct : undefined
          }
        >
          {editor}
        </StorefrontProvider>
      ) : (
        editor
      )}
      {editingProduct !== undefined && siteId && ownStore.data ? (
        <ProductEditor
          siteId={siteId}
          currency={ownStore.data.currency}
          product={editingProduct}
          collections={[
            ...new Set(
              (ownProducts.data ?? []).map((p) => p.collection).filter(Boolean),
            ),
          ]}
          onClose={() => setEditingProduct(undefined)}
        />
      ) : null}
      <PublishSuccessModal
        open={publishModalOpen}
        onClose={() => setPublishModalOpen(false)}
        siteName={siteDetails?.name || "Your Website"}
        slug={siteDetails?.slug || siteId || "site"}
        versionNumber={publishedVersion}
        onGoToDashboard={() => navigate("/app/dashboard")}
      />
    </>
  );
}

export function PreviewPage() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<SiteDocument | null>(null);
  const [siteDetails, setSiteDetails] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewport, setViewport] = useState<"desktop" | "tablet" | "mobile">(
    "desktop",
  );
  const [publishing, setPublishing] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishedVersion, setPublishedVersion] = useState<number | undefined>(
    undefined,
  );
  const [activePageId, setActivePageId] = useState<string | undefined>(
    undefined,
  );
  const [activeStoreRoute, setActiveStoreRoute] = useState<StoreRoute | null>(
    null,
  );
  const template = useTemplate(siteDetails?.templateId);
  const previewStore = usePreviewStore(siteDetails, template.data, doc);

  const loadPreviewDocument = async () => {
    if (!siteId) return;
    setLoading(true);

    try {
      const siteData = await apiFetch<Site>(`/sites/${siteId}`);
      setSiteDetails(siteData);
    } catch (err) {
      console.warn("Could not load site details for preview:", err);
    }

    // 1. Memory Store Check
    const storeState = useEditorStore.getState();
    if (
      storeState.siteId === siteId &&
      storeState.document?.pages?.length > 0
    ) {
      setDoc(storeState.document);
      setLoading(false);
      return;
    }

    // 2. Local Storage Cache Check
    const localDoc = loadDraftFromStorage(siteId);
    if (localDoc) {
      setDoc(localDoc);
      setLoading(false);
      return;
    }

    // 3. Server Backend API Check
    try {
      const draft = await apiFetch<ServerDraftResponse>(
        `/sites/${siteId}/draft`,
      );
      if (draft?.document) {
        const parsed = parseDocumentPayload(draft.document);
        if (parsed) {
          setDoc(parsed);
          saveDraftToStorage(siteId, parsed);
        }
      }
    } catch (err) {
      console.error("Failed to load draft for preview from backend:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPreviewDocument();
  }, [siteId]);

  const handlePublish = async () => {
    if (!siteId) return;
    setPublishing(true);

    // Auto-save active document before publishing
    const currentDoc = useEditorStore.getState().document || doc;
    if (currentDoc) {
      saveDraftToStorage(siteId, currentDoc);
      try {
        await apiFetch<ServerDraftResponse>(`/sites/${siteId}/draft`, {
          method: "PUT",
          body: JSON.stringify({
            revision: 1,
            schemaVersion: 1,
            document: currentDoc,
          }),
        });
      } catch (err) {
        console.warn("Autosave before preview publish failed:", err);
      }
    }

    try {
      const version = await apiFetch<{ versionNumber: number }>(
        `/sites/${siteId}/publish`,
        {
          method: "POST",
        },
      );
      setPublishedVersion(version.versionNumber);
      setPublishModalOpen(true);
    } catch (err: unknown) {
      alert(
        `Publishing failed: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-center" style={{ minHeight: "100vh" }}>
        <Spinner size="lg" />
        <h2>Loading Preview</h2>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="loading-center" style={{ minHeight: "100vh" }}>
        <AlertCircle size={40} style={{ color: "var(--z-error-500)" }} />
        <h2>Preview Unavailable</h2>
        <p>Could not load document for preview.</p>
        <div style={{ display: "flex", gap: "12px", marginTop: "16px" }}>
          <Button variant="secondary" onClick={() => loadPreviewDocument()}>
            <RefreshCw size={16} />
            Try Again
          </Button>
          <Button
            variant="primary"
            onClick={() => navigate(`/app/sites/${siteId}/editor`)}
          >
            <ArrowLeft size={16} />
            Return to Editor
          </Button>
        </div>
      </div>
    );
  }

  const devices = [
    { id: "desktop" as const, label: "Desktop", width: 1280, icon: Monitor },
    { id: "tablet" as const, label: "Tablet", width: 768, icon: Tablet },
    { id: "mobile" as const, label: "Mobile", width: 375, icon: Smartphone },
  ];

  const activeDevice = devices.find((d) => d.id === viewport) ?? devices[0];
  const activePage =
    doc?.pages?.find((p) => p.id === activePageId) ?? doc?.pages?.[0];
  const appendCatalog =
    previewStore &&
    activePage?.slug === "/shop" &&
    !hasCatalogSlot(activePage.nodes || []);

  const handleNavigate = (href: Href) => {
    setActiveStoreRoute(null);
    if (!doc?.pages?.length) return;
    if (href.type === "page" && href.pageId) {
      const target = doc.pages.find((p) => p.id === href.pageId);
      if (target) {
        setActivePageId(target.id);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else if (href.type === "url" && href.url) {
      if (href.url === "/" || href.url === "") {
        const home = doc.pages.find((p) => p.slug === "/") ?? doc.pages[0];
        if (home) setActivePageId(home.id);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else if (href.url.startsWith("/")) {
        const matching = doc.pages.find(
          (p) => p.slug === href.url || p.slug === href.url.replace(/\/$/, ""),
        );
        if (matching) {
          setActivePageId(matching.id);
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else if (href.url.includes("/shop")) {
          const shop = doc.pages.find((p) => p.slug === "/shop");
          if (shop) setActivePageId(shop.id);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      } else if (href.url.startsWith("#")) {
        const el = document.getElementById(href.url.slice(1));
        el?.scrollIntoView({ behavior: "smooth" });
      } else if (
        href.url.startsWith("http://") ||
        href.url.startsWith("https://") ||
        href.url.startsWith("mailto:") ||
        href.url.startsWith("tel:")
      ) {
        window.open(href.url, "_blank", "noopener,noreferrer");
      }
    }
  };

  const handleStoreGo = (path: string) => {
    const route = storeRouteFor(path);
    if (route) {
      setActiveStoreRoute(route);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setActiveStoreRoute(null);
    if (path.includes("/shop")) {
      const shop = doc?.pages?.find((p) => p.slug === "/shop");
      if (shop) setActivePageId(shop.id);
    } else {
      const home = doc?.pages?.find((p) => p.slug === "/") ?? doc?.pages?.[0];
      if (home) setActivePageId(home.id);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        background: "#0f172a",
        color: "#ffffff",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 24px",
          background: "#1e293b",
          borderBottom: "1px solid #334155",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          className="btn btn--ghost"
          style={{ color: "#e2e8f0", borderColor: "#475569" }}
          onClick={() => navigate(`/app/sites/${siteId}/editor`)}
        >
          <ArrowLeft size={16} />
          Back to Editor
        </button>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "4px",
              padding: "3px",
              background: "#0f172a",
              borderRadius: "var(--z-radius-md)",
              border: "1px solid #334155",
            }}
          >
            {devices.map((d) => {
              const Icon = d.icon;
              return (
                <button
                  key={d.id}
                  type="button"
                  className={`btn btn--sm ${viewport === d.id ? "" : "btn--ghost"}`}
                  style={
                    viewport === d.id
                      ? {
                          background: "var(--z-brand-600)",
                          color: "#fff",
                          borderColor: "var(--z-brand-600)",
                        }
                      : { color: "#94a3b8", borderColor: "transparent" }
                  }
                  onClick={() => setViewport(d.id)}
                >
                  <Icon size={14} />
                  {d.label}
                </button>
              );
            })}
          </div>

          {doc.pages && doc.pages.length > 1 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "3px",
                background: "#0f172a",
                borderRadius: "var(--z-radius-md)",
                border: "1px solid #334155",
              }}
            >
              <span
                style={{ fontSize: "12px", color: "#94a3b8", padding: "0 6px" }}
              >
                Pages:
              </span>
              {doc.pages.map((p) => {
                const isCurrent = activePage?.id === p.id && !activeStoreRoute;
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`btn btn--sm ${isCurrent ? "" : "btn--ghost"}`}
                    style={
                      isCurrent
                        ? {
                            background: "var(--z-brand-600)",
                            color: "#fff",
                            borderColor: "var(--z-brand-600)",
                          }
                        : { color: "#94a3b8", borderColor: "transparent" }
                    }
                    onClick={() => {
                      setActiveStoreRoute(null);
                      setActivePageId(p.id);
                    }}
                  >
                    {p.navLabel || p.title || p.slug}
                  </button>
                );
              })}
              {activeStoreRoute && (
                <button
                  type="button"
                  className="btn btn--sm"
                  style={{ background: "var(--z-brand-600)", color: "#fff" }}
                  onClick={() => setActiveStoreRoute(null)}
                >
                  Product view ✕
                </button>
              )}
            </div>
          )}
        </div>

        <Button variant="primary" loading={publishing} onClick={handlePublish}>
          <Rocket size={16} />
          {publishing ? "Publishing…" : "Publish Website"}
        </Button>
      </header>

      <main
        style={{
          flex: 1,
          minHeight: 0,
          padding: "32px",
          overflowY: "auto",
          overflowX: "auto",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: `${activeDevice.width}px`,
            maxWidth: "100%",
            background: "#ffffff",
            borderRadius: "8px",
            flexShrink: 0,
            margin: "0 auto",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5)",
            transition: "width 0.3s ease",
          }}
        >
          {previewStore ? (
            <StorefrontProvider
              store={previewStore}
              interactive={true}
              onGo={handleStoreGo}
            >
              <DocumentRenderer
                document={doc}
                pageId={activePage?.id}
                activePage={
                  activeStoreRoute
                    ? { id: "store-route-page", slug: "", title: "", nodes: [] }
                    : undefined
                }
                mode="preview"
                breakpoint={viewport}
                onNavigate={handleNavigate}
                renderSlot={renderStoreSlot}
                headerActions={<HeaderActions />}
                pageAddon={
                  activeStoreRoute ? (
                    renderStoreRoute(activeStoreRoute)
                  ) : appendCatalog ? (
                    <div className="zs-page">
                      <Catalog />
                    </div>
                  ) : undefined
                }
              />
            </StorefrontProvider>
          ) : (
            <DocumentRenderer
              document={doc}
              pageId={activePage?.id}
              mode="preview"
              breakpoint={viewport}
              onNavigate={handleNavigate}
            />
          )}
        </div>
      </main>

      <PublishSuccessModal
        open={publishModalOpen}
        onClose={() => setPublishModalOpen(false)}
        siteName={siteDetails?.name || "Your Website"}
        slug={siteDetails?.slug || siteId || "site"}
        versionNumber={publishedVersion}
        onGoToDashboard={() => navigate("/app/dashboard")}
      />
    </div>
  );
}

export function MediaPage() {
  return <Placeholder title="Media" hint="Media library arrives in Phase 5." />;
}

export function SettingsPage() {
  return <Placeholder title="Settings" />;
}
